import React from 'react';
import styled from 'styled-components';

interface ProductCardItemProps {
  icon?: React.ReactNode;
  imageUrl?: string;
  title: string;
  description?: string;
  onClick: () => void;
  titleSize?: number;
  titleWeight?: number;
}

const Container = styled.div`
  display: flex;
  min-height: 64px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid var(--Neutral-200-tint, #CCC);
  background: var(--Base-White, #FFF);
  cursor: pointer;
  overflow: hidden;

  &:hover {
    border-radius: 8px;
    border: 1px solid var(--Neutral-200-tint, #CCC);
    background: var(--Primary-020-tint, #F1FBFE);
  }

  &:active {
    border-radius: 8px;
    border: 1px solid var(--Neutral-200-tint, #CCC);
    background: var(--Primary-100-tint, #D4E9F2);
  }
`;

const ImageContainer = styled.div`
  display: flex;
  width: 100%;
  height: 160px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  background: var(--Base-White, #FFF);
`;

const IconContainer = styled.div`
  display: flex;
  width: 100%;
  height: 160px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
`;

const Image = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
  padding: 8px;
  box-sizing: border-box;
`;

const Title = styled.div`
  overflow: hidden;
  color: var(--Base-Black, #1F1F1F);
  font-feature-settings: 'liga' off;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Lato;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

const Description = styled.div`
  overflow: hidden;
  color: var(--Neutral-600-shade, #666);
  font-feature-settings: 'liga' off;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Lato;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const ProductCardItem: React.FC<ProductCardItemProps> = ({
  icon,
  imageUrl,
  title,
  description,
  onClick,
  titleSize,
  titleWeight
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Container
      onClick={onClick}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="button"
      aria-label={title}
    >
      {imageUrl ? (
        <ImageContainer>
          <Image src={imageUrl} alt={title} />
        </ImageContainer>
      ) : icon ? (
        <IconContainer>
          <IconWrapper>
            {icon}
          </IconWrapper>
        </IconContainer>
      ) : null}
      <TextContainer>
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
      </TextContainer>
    </Container>
  );
};

export default ProductCardItem;
